import { useEffect, useRef, useState, type RefObject } from "react";
import { Link } from "react-router-dom";
import type { CapabilityDefinition, CapabilityRow, LifecycleStage } from "@apf/framework-config";
import { businessModulePath, domainWorkblockPath, type BusinessModule, type DomainWorkblock } from "../catalogue";
import { useCopy } from "../framework";
import { iconFor } from "./icons";

const ModuleIcon = iconFor("blocks");
const RailsIcon = iconFor("network");

/* The overview is the hero's product visual and is also shipped inside the
 * portable Workblock, so its composition follows the component's available
 * width rather than the viewport (the CapabilityOverview convention). */
const narrowOverviewQuery = "(max-width: 42rem)";
const narrowOverviewWidthRem = 40;

function useMediaQuery(queryText: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia(queryText).matches
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(queryText);
    const sync = () => setMatches(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, [queryText]);
  return matches;
}

function useNarrowOverview(): [RefObject<HTMLDivElement | null>, boolean] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia(narrowOverviewQuery).matches
  );
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const sync = (width: number) => setIsNarrow(width < narrowOverviewWidthRem * rootFontSize);
    sync(element.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries.find(({ target }) => target === element);
      if (entry) sync(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, isNarrow];
}

/* One domain Workblock in the glance layer: the linked system name and its
 * one-line scope stay visible; the five business modules sit behind a native
 * disclosure so the first read is nine systems, not forty-five rows. Print
 * and other expanded contexts render the same list statically, so no content
 * exists only behind interaction. */
function OverviewDomain({
  domainWorkblock,
  businessModules,
  expanded
}: {
  domainWorkblock: DomainWorkblock;
  businessModules: BusinessModule[];
  expanded: boolean;
}) {
  const copy = useCopy();
  const DomainIcon = iconFor(domainWorkblock.icon);
  const name = copy(domainWorkblock.nameKey);
  const moduleListLabel = `${copy("home.overview.modules-in", "Business modules in")} ${name}`;
  const moduleList = (
    <ul className="apf-overview__modules" aria-label={moduleListLabel}>
      {businessModules.map((businessModule) => (
        <li key={businessModule.id}>
          <Link to={businessModulePath(domainWorkblock.id, businessModule.id)}>
            <ModuleIcon size={16} />
            <span>{copy(businessModule.labelKey)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
  return (
    <article className="apf-overview__domain">
      <Link className="apf-overview__system" to={domainWorkblockPath(domainWorkblock.id)}>
        <DomainIcon size={20} />
        <span className="apf-overview__system-name">
          {name}
          {domainWorkblock.signatureKey ? (
            <span className="apf-system-signature apf-system-signature--map">{copy(domainWorkblock.signatureKey)}</span>
          ) : null}
        </span>
      </Link>
      <p className="apf-overview__domain-label">{copy(domainWorkblock.labelKey)}</p>
      {expanded ? moduleList : (
        <details className="apf-overview__detail">
          <summary aria-label={moduleListLabel}>
            <span className="apf-overview__detail-count">{businessModules.length} {copy("home.overview.module-count", "business modules")}</span>
          </summary>
          {moduleList}
        </details>
      )}
    </article>
  );
}

/**
 * The homepage framework overview, read in two layers. The glance layer keeps
 * the whole semantic model visible: five lifecycle stages in working order,
 * each stage's domain Workblocks with their governed names, the three shared
 * capability rows and the one execution foundation. The detail layer keeps
 * every business module one native disclosure away, and every name remains a
 * stable catalogue link. Narrow containers keep the same model as a
 * five-stage accordion in lifecycle order; print renders everything expanded.
 */
export function HomeOverview({
  stages,
  domainWorkblockById,
  businessModulesByDomainWorkblock,
  capabilityRows,
  capabilityById
}: {
  stages: LifecycleStage[];
  domainWorkblockById: Map<string, DomainWorkblock>;
  businessModulesByDomainWorkblock: Map<string, BusinessModule[]>;
  capabilityRows: CapabilityRow[];
  capabilityById: Map<string, CapabilityDefinition>;
}) {
  const copy = useCopy();
  const [rootRef, isNarrow] = useNarrowOverview();
  const isPrint = useMediaQuery("print");
  const stageAccordion = isNarrow && !isPrint;
  return (
    <div className="apf-overview" ref={rootRef}>
      <ol className="apf-overview__stages">
        {stages.map((stage) => {
          const header = (
            <div className="apf-overview__stage-header">
              <span className="apf-overview__stage-tick" aria-hidden="true" />
              <span className="apf-overview__stage-index">0{stage.order}</span>
              <h3>{copy(stage.labelKey)}</h3>
            </div>
          );
          const domains = (
            <div className="apf-overview__domains">
              {stage.domainWorkblockIds.map((domainWorkblockId) => {
                const domainWorkblock = domainWorkblockById.get(domainWorkblockId);
                if (!domainWorkblock) return null;
                return (
                  <OverviewDomain
                    key={domainWorkblockId}
                    domainWorkblock={domainWorkblock}
                    businessModules={businessModulesByDomainWorkblock.get(domainWorkblockId) ?? []}
                    expanded={isPrint}
                  />
                );
              })}
            </div>
          );
          return (
            <li className="apf-overview__stage" key={stage.id}>
              {stageAccordion ? (
                <details className="apf-overview__stage-detail">
                  <summary aria-label={`${copy("home.overview.stage", "Stage")} 0${stage.order}: ${copy(stage.labelKey)}`}>
                    {header}
                    <span className="apf-overview__detail-mark" aria-hidden="true" />
                  </summary>
                  {domains}
                </details>
              ) : (
                <>
                  {header}
                  {domains}
                </>
              )}
            </li>
          );
        })}
      </ol>
      <div className="apf-overview__rows">
        {capabilityRows.map((row) => (
          <div className="apf-overview__row" key={row.id}>
            <h3>{copy(row.labelKey)}</h3>
            <ul>
              {row.capabilityIds.map((capabilityId) => {
                const capability = capabilityById.get(capabilityId);
                if (!capability) return null;
                const Icon = iconFor(capability.icon);
                return (
                  <li key={capabilityId}>
                    {capabilityId === "office-composer-conductor" ? (
                      <Link className="apf-overview__capability-link" to="/capabilities">
                        <Icon size={16} />
                        <span>{copy(capability.labelKey)}</span>
                      </Link>
                    ) : (
                      <><Icon size={16} /><span>{copy(capability.labelKey)}</span></>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      {/* The trust and certification statement has one canonical placement,
        * the platform-structure page; the overview names the foundation and
        * routes there. */}
      <div className="apf-overview__foundation">
        <p className="apf-overview__foundation-name"><RailsIcon size={16} /><span>{copy("home.overview.foundation-name", "One execution foundation")}</span></p>
        <p className="apf-overview__foundation-trust"><Link to="/architecture">{copy("page.architecture.title", "The platform structure")}</Link></p>
      </div>
    </div>
  );
}
